import {
  Body,
  Button,
  Caption,
  Card,
  Col,
  CustomLink,
  Divider,
  Flex,
  LazyImage,
  Row,
  Shape,
  SubTitle,
  Tag,
  Tooltip,
  styled,
} from '@teko/ds-desktop';
import {
  CashColored,
  CautionOutlined,
  DownOutlined,
  GiftColored,
  VoucherColored,
} from '@teko/ds-icon';
import { ProductPrice, formatCurrency } from '@teko/teko-ecommerce';
import { TrackingObject, getTrackingData } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/dist/shared';
import { useCartV2, useTenantConfig } from 'contexts';
import { countAndGetBenefitDescription } from 'helpers';
import { t } from 'i18n';
import React, { useMemo } from 'react';
import { IActiveCombo, IConditionProduct, IPromotionBenefit } from 'types';
import { ComboProductPrice } from './ComboProductPrice';
import { PromotionBenefitInfo } from './PromotionBenefitInfo';

const MAX_COL_NUMBER = 5;

interface StyledBgPromotionProps {
  bgColor: string;
  size?: string;
}

interface ComboItemProps {
  index: number;
  combo: IActiveCombo;
  onClickProductChange: (selectedSku: string, conditionKey: number) => void;
}

interface ResidualProducts {
  key: number;
  requiredQuantity: number;
  product: IConditionProduct;
  tracking: TrackingObject;
}

export const ComboItemNew: React.FC<ComboItemProps> = ({
  index,
  combo,
  onClickProductChange,
}) => {
  const { totalProduct, promotion, conditions, totalAmount, totalDiscount } =
    combo;
  const { onAddComboItems } = useCartV2();
  const { numberDisplayFormat, epicOmni1643 } = useTenantConfig();
  const numberOfColumns =
    conditions.length + (promotion.benefit ? 1 : 0) > MAX_COL_NUMBER
      ? MAX_COL_NUMBER
      : conditions.length + (promotion.benefit ? 1 : 0);
  const span = 12 / numberOfColumns;

  const productColumnNumber = numberOfColumns - (promotion.benefit ? 1 : 0);
  const isNotEnoughProductColumn = conditions.length > productColumnNumber;
  const residualProductQuantity = useMemo(() => {
    if (isNotEnoughProductColumn) {
      let result = 0;
      for (let index = 0; index < productColumnNumber - 1; index++) {
        result += conditions[index].requiredQuantity;
      }
      return totalProduct - result;
    }
    return 0;
  }, [JSON.stringify(conditions), isNotEnoughProductColumn]);

  const addComboToCart = async () => {
    const mainPayload = {
      override: true,
      groups: [
        {
          quantity: 1,
          promotionId: promotion.id,
          products: conditions.map((el, index) => ({
            sku: el.product.sku,
            promotionId: !!el.product.bestPromotionIds?.length
              ? el.product.bestPromotionIds?.[0]
              : null,
            quantity: el.requiredQuantity,
            isPrimary: index === 0,
          })),
        },
      ],
    };
    await onAddComboItems(mainPayload, false);
  };

  const BenefitItem = ({ benefit }: { benefit: IPromotionBenefit }) => {
    const { numberOfBenefit } = countAndGetBenefitDescription(benefit);
    const { discount, voucher, gifts } = benefit;
    const discountValue = discount?.flat || discount?.percent || 0;

    const BenefitTag = () => (
      <StyledTag
        type="ghost"
        variant="branding"
        content={
          numberOfBenefit === 1 && discountValue > 0
            ? t('Giảm ngay')
            : t('Tặng ngay')
        }
      />
    );

    const GiftTooltipContent = ({
      gifts,
      promotionBenefit,
    }: {
      gifts?: Array<{
        name?: string;
        image?: string;
        quantity?: number;
        icon?: React.ReactNode;
      }>;
      promotionBenefit?: IPromotionBenefit;
    }) => {
      return (
        <Card
          header={
            <Caption level={2} color="textSecondary">
              {t('Bạn nhận được').toLocaleUpperCase()}
            </Caption>
          }
        >
          <PromotionBenefitInfo
            gifts={gifts}
            promotionBenefit={promotionBenefit}
          />
        </Card>
      );
    };

    return (
      <PromotionWrapper>
        {numberOfBenefit <= 1 ? (
          <>
            {discountValue > 0 && (
              <>
                <StyledBgPromotion bgColor="#E7FBED">
                  <CashColored size={60} />
                </StyledBgPromotion>
                <StyledWrapperTag>
                  <BenefitTag />
                  {discount?.flat ? (
                    <ProductPrice
                      latestPrice={discountValue}
                      numberDisplayFormat={numberDisplayFormat}
                    />
                  ) : (
                    <>
                      <SubTitle
                        level={1}
                        color="primary500"
                      >{`${discountValue}%`}</SubTitle>
                      {discount?.maxAmount && (
                        <>
                          {[
                            t('Giảm tối đa'),
                            formatCurrency(
                              discount.maxAmount,
                              numberDisplayFormat,
                            ),
                          ].map((item) => {
                            return (
                              <StyledProductName
                                key={`${item}`}
                                level={1}
                                line={2}
                                color="primary500"
                                style={{
                                  marginTop: '-4px',
                                }}
                              >
                                {`${item}`}
                              </StyledProductName>
                            );
                          })}
                        </>
                      )}
                    </>
                  )}
                </StyledWrapperTag>
              </>
            )}
            {voucher?.name && (
              <>
                <StyledBgPromotion bgColor="#FFF2F0">
                  <VoucherColored size={60} />
                </StyledBgPromotion>
                <StyledWrapperTag>
                  <BenefitTag />
                  <StyledProductName level={1} line={2}>
                    {voucher.quantity > 1 ? `${voucher.quantity} x ` : ''}
                    {voucher?.name}
                  </StyledProductName>
                </StyledWrapperTag>
              </>
            )}
            {!!gifts?.length && (
              <StyledTooltip
                title={<GiftTooltipContent gifts={gifts.slice(1)} />}
                backgroundColor="white"
                // Not show tooltip when has only a gift
                show={gifts.slice(1).length > 0}
                triggerHover={gifts.slice(1).length > 0}
              >
                <div>
                  <LazyImage
                    src={gifts[0].imageUrl || ''}
                    width={125}
                    height={125}
                    contain
                    borderRadius={1}
                  />
                  <StyledWrapperTag>
                    <BenefitTag />
                    <StyledProductName level={1} line={2} color="#82869E">
                      {gifts[0].name || gifts[0].sku}
                    </StyledProductName>
                  </StyledWrapperTag>
                </div>
              </StyledTooltip>
            )}
          </>
        ) : (
          <StyledTooltip
            title={<GiftTooltipContent promotionBenefit={benefit} />}
            backgroundColor="white"
          >
            <div>
              <StyledBgPromotion bgColor="#FFF2F0">
                <GiftColored size={60} />
              </StyledBgPromotion>
              <StyledWrapperTag>
                <BenefitTag />
                <StyledProductName level={1} line={2} color="#434657">
                  {t('{{numberOfBenefit}} phần quà', {
                    numberOfBenefit,
                  })}
                </StyledProductName>
              </StyledWrapperTag>
            </div>
          </StyledTooltip>
        )}
      </PromotionWrapper>
    );
  };

  const ProductTooltipContent = ({
    residualProducts,
  }: {
    residualProducts: Array<ResidualProducts>;
  }) => {
    const span = 12 / residualProducts.length;
    return (
      <Card>
        <Row gutter={16}>
          {residualProducts.map((item, index) => (
            <Col
              span={span}
              key={index}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <CustomLink href={`/products/${item.product.sku}`}>
                <ItemWrapper>
                  <ConditionItem
                    conditionKey={item.key}
                    product={item.product}
                    requiredQuantity={item.requiredQuantity}
                    isCompactItem={false}
                    tracking={item.tracking}
                  />
                </ItemWrapper>
              </CustomLink>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const ConditionItem = (item: {
    conditionKey: number;
    requiredQuantity: number;
    product: IConditionProduct;
    isCompactItem: boolean;
    residualProducts?: Array<ResidualProducts>;
    tracking: TrackingObject;
  }) => (
    <>
      <StyledLazyImage>
        <LazyImage
          borderRadius={4}
          src={item.product.imageUrl}
          hover="zoom"
          contain
          aspectRatio={1}
          alt={item.product.name}
        />
        {item.isCompactItem && !!item.residualProducts?.length && (
          <StyledTooltip
            title={
              <ProductTooltipContent residualProducts={item.residualProducts} />
            }
            backgroundColor="white"
          >
            <StyledRemainBg>+{residualProductQuantity}</StyledRemainBg>
          </StyledTooltip>
        )}
        {item.requiredQuantity > 1 && (
          <QuantityTag backgroundColor="primary500">
            {item.requiredQuantity}x
          </QuantityTag>
        )}
      </StyledLazyImage>
      <StyledProductName
        level={1}
        line={2}
        color={item.isCompactItem ? '#82869E' : 'textPrimary'}
        style={
          item.isCompactItem
            ? {
                textAlign: 'center',
              }
            : undefined
        }
      >
        {item.isCompactItem
          ? t('và {{var0}} sản phẩm khác', {
              var0: residualProductQuantity,
            })
          : item.product.name}
      </StyledProductName>
      {item.isCompactItem ? null : (
        <ComboProductPrice
          price={item.product.price}
          promotion={promotion}
          textColor="textPrimary"
        />
      )}
      {item.conditionKey > 1 && !item.isCompactItem ? (
        <Flex
          marginTop="s8"
          align="center"
          onClick={(event) => {
            event.preventDefault();
            onClickProductChange(item.product.sku, item.conditionKey);
          }}
          {...item.tracking}
        >
          <Caption color="link500" level={1}>
            {t('Thay đổi sản phẩm')}
          </Caption>
          <DownOutlined color="link500" size={16} />
        </Flex>
      ) : null}
    </>
  );

  return (
    <Card
      footer={
        <>
          <Row justify="space-between" align="bottom">
            <Col>
              {!!totalDiscount && !epicOmni1643 && (
                <Row justify="start">
                  <Caption
                    paddingRight="s28"
                    color="textSecondary"
                    level={1}
                  >{`${t('Tiết kiệm')}:`}</Caption>
                  <Caption color="textSecondary" level={1}>
                    {formatCurrency(totalDiscount, numberDisplayFormat)}
                  </Caption>
                </Row>
              )}
              {/* This section display total amount */}
              <Row justify="start" marginTop="s8" align="bottom">
                <Body marginRight="s16" level={1} color="textTitle">{`${t(
                  'Tổng tiền',
                )}:`}</Body>
                <SubTitle color="primary500" level={1}>
                  {formatCurrency(totalAmount, numberDisplayFormat)}
                </SubTitle>
              </Row>
              {!!totalDiscount && epicOmni1643 && (
                <Row justify="start" align="bottom">
                  <Body marginRight="s16" color="#389E0D" level={1}>{`${t(
                    'Tiết kiệm',
                  )}:`}</Body>
                  <Body color="#389E0D" level={1}>
                    {formatCurrency(totalDiscount, numberDisplayFormat)}
                  </Body>
                </Row>
              )}
            </Col>
            <Button
              variant="secondary"
              onClick={addComboToCart}
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'productComboList',
                contentName: 'btnAddToCart',
                payload: JSON.stringify({
                  promotionType: 'combo',
                  comboTab: `Combo ${totalProduct} sản phẩm`,
                  comboPromotion: promotion.id,
                  comboIndex: index + 1,
                  skus: conditions.map((con) => con.product.sku),
                }),
              })}
            >
              <SubTitle level={2}>{t('Thêm vào giỏ hàng')}</SubTitle>
            </Button>
          </Row>
          {/* check if the promotion is applicable with installment payment */}
          {!!promotion.condition?.exclusions?.find((exclusion) =>
            exclusion.paymentMethods?.includes('VNPAY_INSTALLMENT_CREDIT'),
          ) && (
            <Shape
              height={28}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
              backgroundColor="pending50"
              border="1px solid pending100"
              marginTop="s12"
              paddingLeft="s12"
              borderRadius="s"
            >
              <CautionOutlined color="pending600" size={16} />
              <Body level={1} color="pending600" marginLeft="s8">
                {t('Không áp dụng cùng chương trình trả góp')}
              </Body>
            </Shape>
          )}
        </>
      }
      padding={['s16', 's40']}
    >
      <Row style={{ justifyContent: 'space-evenly' }}>
        {conditions.map(({ product, requiredQuantity, key }, index) => {
          const isCompactItem =
            isNotEnoughProductColumn && index === productColumnNumber - 1;
          if (isNotEnoughProductColumn && index >= productColumnNumber)
            return null;
          const isLastItem = isNotEnoughProductColumn
            ? index === productColumnNumber - 1
            : index === conditions.length - 1;
          const residualProducts = isNotEnoughProductColumn
            ? conditions
                .slice(productColumnNumber - 1)
                .map((residualProduct) => ({
                  ...residualProduct,
                  tracking: getTrackingData({
                    isTrackContent: true,
                    regionName: 'productComboList',
                    contentName: 'btnChangeProductCombo',
                    payload: JSON.stringify({
                      comboTab: `Combo ${totalProduct} sản phẩm`,
                      promotionType: 'combo',
                      skuIndex: index + 1,
                      sku: product.sku,
                    }),
                  }),
                }))
            : [];
          const conditionItem = (
            <ConditionItem
              conditionKey={key}
              product={product}
              requiredQuantity={requiredQuantity}
              isCompactItem={isCompactItem}
              residualProducts={residualProducts}
              tracking={getTrackingData({
                isTrackContent: true,
                regionName: 'productComboList',
                contentName: 'btnChangeProductCombo',
                payload: JSON.stringify({
                  comboTab: `Combo ${totalProduct} sản phẩm`,
                  promotionType: 'combo',
                  skuIndex: index + 1,
                  sku: product.sku,
                }),
              })}
            />
          );

          return (
            <React.Fragment key={index}>
              <StyledProductCol
                span={span}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
                className={`${!isLastItem ? 'not-last-item' : ''} ${
                  numberOfColumns === MAX_COL_NUMBER ? 'full-col-width' : ''
                }`}
              >
                {!!isCompactItem ? (
                  <ItemWrapper>{conditionItem}</ItemWrapper>
                ) : (
                  <CustomLink href={`/products/${product.sku}`}>
                    <ItemWrapper>{conditionItem}</ItemWrapper>
                  </CustomLink>
                )}
              </StyledProductCol>
            </React.Fragment>
          );
        })}
        {promotion.benefit && (
          <StyledPromotionCol
            span={span}
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
            className={
              numberOfColumns === MAX_COL_NUMBER ? 'full-col-width' : ''
            }
          >
            <BenefitItem benefit={promotion.benefit} />
          </StyledPromotionCol>
        )}
      </Row>
      <Divider dividerStyle={DividerStyleEnum.dashed} color="divider" />
    </Card>
  );
};

const StyledProductCol = styled(Col)`
  position: relative;
  &.not-last-item {
    &:after {
      content: '+';
      position: absolute;
      right: -4px;
      top: 60px;
      color: #a7abc3;
      font-size: 18px;
      cursor: auto;
    }
  }
  &.not-last-item.full-col-width {
    &:after {
      content: '+';
      position: absolute;
      right: -24px;
      top: 60px;
      color: #a7abc3;
      font-size: 18px;
      cursor: auto;
    }
  }
`;

const StyledPromotionCol = styled(Col)`
  &:before {
    content: ' ';
    position: absolute;
    left: 0;
    border-right: 1px solid #e4e5f0;
    height: 125px;
  }
  &.full-col-width {
    &:before {
      content: ' ';
      position: absolute;
      left: -20px;
      color: #a7abc3;
      border-right: 1px solid #a7abc3;
      height: 125px;
    }
  }
`;

const ItemWrapper = styled.div`
  position: relative;
  width: 125px;
  cursor: pointer;
`;

const StyledLazyImage = styled.div`
  position: relative;
`;

const QuantityTag = styled(Shape)`
  position: absolute;
  height: 16px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: white;
  padding: 2px 5px;
  top: 8px;
  left: 8px;
`;

const StyledProductName = styled(Caption)`
  margin-top: 8px;
`;

const StyledRemainBg = styled.div`
  position: absolute;
  width: 125px;
  height: 125px;
  background-color: rgba(27, 29, 41, 0.5);
  color: #ffffff;
  z-index: 1;
  top: 0;
  left: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PromotionWrapper = styled.div`
  position: relative;
  width: 125px;
`;

const StyledWrapperTag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
`;

const StyledTag = styled(Tag)`
  text-transform: uppercase;
`;

const StyledBgPromotion = styled.div<StyledBgPromotionProps>(
  ({ bgColor, size = '125px' }: StyledBgPromotionProps) => `
  width: ${size};
  height: ${size};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${bgColor};
  border-radius: 4px;
`,
);

const StyledTooltip = styled(Tooltip)`
  box-shadow: 1px 1px 3px 0px #e6e6e6;
  width: 420px;
  padding: 16px 20px;
`;
